import { create } from 'zustand';

interface ModeCache {
  sessionId: string | null;
  messages: any[];
  todos: any[];
  totalTokens: number;
  totalTurns: number;
  elapsedMs: number;
  agentRunning: boolean;
  thinkingPhase: string;
  llmModel: string;
  llmStatus: string;
}

interface WorkspaceState {
  sessionId: string | null;
  messages: any[];
  todos: any[];
  totalTokens: number;
  totalTurns: number;
  elapsedMs: number;
  agentRunning: boolean;
  thinkingPhase: string;
  chatInput: string;
  devMode: string;
  llmModel: string;
  llmStatus: string;
  modeCache: Record<string, ModeCache>;
  _projectId: string;
  interruptedSession: any | null;  // 被中断的会话信息（用于提示用户继续）
  needConfirm: boolean;  // 是否需要用户确认（interrupted/需要确认的状态）

  setSessionId: (id: string | null) => void;
  addMsg: (role: string, content: string) => void;
  setMessages: (msgs: any[]) => void;
  prependMessages: (msgs: any[]) => void;
  setTodos: (todos: any[]) => void;
  addTokens: (amount: number) => void;
  setTotalTokens: (val: number) => void;
  setTotalTurns: (val: number) => void;
  setElapsedMs: (val: number) => void;
  setAgentRunning: (val: boolean) => void;
  setThinkingPhase: (val: string) => void;
  setChatInput: (val: string) => void;
  setDevMode: (val: string) => void;
  setLlmStatus: (model: string, status: string) => void;
  setLlmModel: (model: string) => void;
  resetMessages: () => void;
  restore: (data: any) => void;
  saveModeCache: (mode: string, projectId?: string) => void;
  restoreModeCache: (mode: string, projectId?: string) => boolean;
  setInterruptedSession: (session: any | null) => void;
  setNeedConfirm: (val: boolean) => void;
  setProjectId: (pid: string) => void;
}

const initial = {
  sessionId: null, messages: [], todos: [],
  totalTokens: 0, totalTurns: 0, elapsedMs: 0,
  agentRunning: false, thinkingPhase: '', chatInput: '', devMode: 'development',
  llmModel: '', llmStatus: 'idle', modeCache: {} as Record<string, ModeCache>, _projectId: '',
  interruptedSession: null, needConfirm: false,
};

export const useWorkspaceStore = create<WorkspaceState>()((set, get) => ({
  ...initial,

  setSessionId: (id) => set({ sessionId: id }),
  addMsg: (role, content) => set((s) => ({
    messages: [...s.messages, { id: Date.now() + Math.random().toString(), role, content }],
  })),
  setMessages: (msgs) => set({ messages: msgs }),
  prependMessages: (msgs: any[]) => set((s) => ({ messages: [...msgs, ...s.messages] })),
  setTodos: (todos) => set({ todos }),
  addTokens: (amount) => set((s) => ({ totalTokens: s.totalTokens + amount })),
  setTotalTokens: (val) => set({ totalTokens: val }),
  setTotalTurns: (val) => set({ totalTurns: val }),
  setElapsedMs: (val) => set({ elapsedMs: val }),
  setAgentRunning: (val) => set({ agentRunning: val }),
  setThinkingPhase: (val) => set({ thinkingPhase: val }),
  setChatInput: (val) => set({ chatInput: val }),
  setDevMode: (val) => set({ devMode: val }),
  setLlmStatus: (model, status) => set({ llmModel: model, llmStatus: status }),
  setLlmModel: (model) => set({ llmModel: model }),
  resetMessages: () => set({ messages: [], todos: [], totalTokens: 0, totalTurns: 0, elapsedMs: 0 }),
  restore: (data) => set({
    sessionId: data.sessionId,
    messages: data.messages || [],
    todos: data.todos || [],
    totalTokens: data.totalTokens || 0,
    totalTurns: data.totalTurns || 0,
    elapsedMs: data.elapsedMs || 0,
    agentRunning: false,
    thinkingPhase: '',
    llmStatus: 'idle',
  }),

  saveModeCache: (mode: string, projectId?: string) => {
    const s = get();
    const pid = projectId || s._projectId || '';
    set({
      modeCache: {
        ...s.modeCache,
        [`${pid}:${mode}`]: {
          sessionId: s.sessionId,
          messages: s.messages,
          todos: s.todos,
          totalTokens: s.totalTokens,
          totalTurns: s.totalTurns,
          elapsedMs: s.elapsedMs,
          agentRunning: s.agentRunning,
          thinkingPhase: s.thinkingPhase,
          llmModel: s.llmModel,
          llmStatus: s.llmStatus,
        },
      },
    });
  },

  restoreModeCache: (mode: string, projectId?: string) => {
    const s = get();
    const pid = projectId || s._projectId || '';
    const cached = s.modeCache[`${pid}:${mode}`];
    if (cached) {
      set({
        sessionId: cached.sessionId,
        messages: cached.messages,
        todos: cached.todos,
        totalTokens: cached.totalTokens,
        totalTurns: cached.totalTurns,
        elapsedMs: cached.elapsedMs,
        devMode: mode,
        agentRunning: false,          // 缓存不可信，靠 loadActiveSession 判定
        thinkingPhase: '',
        llmModel: cached.llmModel || '',
        llmStatus: 'idle',
      });
      return true;
    }
    return false;
  },

  setProjectId: (pid: string) => set({ _projectId: pid }),
  setInterruptedSession: (session) => set({ interruptedSession: session, needConfirm: !!session }),
  setNeedConfirm: (val) => set({ needConfirm: val }),
}));
